import { Navigate, useParams } from "@/lib/router-compat";
import { brands } from "@/data/brandData";

/* ------------------------------------------------------------------ */
/*  URL-slug ↔ internal type slug mapping                              */
/* ------------------------------------------------------------------ */

/** URL uses single-dash for diesel variants; Inventory slugifies to triple-dash */
const urlTypeToInternal: Record<string, string> = {
  "motor-home-class-a-diesel": "motor-home-class-a---diesel",
  "motor-home-class-b-diesel": "motor-home-class-b---diesel",
  "motor-home-class-c-diesel": "motor-home-class-c---diesel",
};

export const knownTypeSlugs = new Set([
  "travel-trailer",
  "fifth-wheel",
  "motor-home-class-a",
  "motor-home-class-a-diesel",
  "motor-home-class-b",
  "motor-home-class-b-diesel",
  "motor-home-class-c",
  "motor-home-class-c-diesel",
  "toy-hauler-fifth-wheel",
  "toy-hauler-travel-trailer",
  "folding-pop-up-camper",
  "destination-trailer",
  "expandable",
  "other",
]);

export function isKnownType(slug: string): boolean {
  return knownTypeSlugs.has(slug);
}

function toInternalType(urlSlug: string): string {
  return urlTypeToInternal[urlSlug] || urlSlug;
}

export function typeRedirectUrl(
  type: string,
  condition?: string,
  feature?: string,
): string {
  const p = new URLSearchParams();
  if (condition) p.set("condition", condition);
  p.set("type", toInternalType(type));
  if (feature) p.set("feature", feature);
  return `/rv-search?${p.toString()}`;
}

/* ------------------------------------------------------------------ */
/*  Brand model resolution                                             */
/* ------------------------------------------------------------------ */

// Build slug → searchQuery maps per brand
const brandModelMap = new Map<string, Map<string, string>>();
for (const brand of brands) {
  const mm = new Map<string, string>();
  for (const m of brand.models) {
    mm.set(m.name.toLowerCase().replace(/\s+/g, "-"), m.searchQuery);
  }
  brandModelMap.set(brand.slug, mm);
}

// Ordered longest-first for greedy matching
const TYPE_SUFFIXES = [
  "-motor-home-class-c-diesel-inventory",
  "-motor-home-class-c-inventory",
  "-motor-home-class-c-diesel",
  "-motor-home-class-c",
  "-destination-trailer-inventory",
  "-travel-trailer-inventory",
  "-fifth-wheel-inventory",
  "-destination-trailer",
  "-travel-trailer",
  "-fifth-wheel",
];

function resolveModel(
  brandSlug: string,
  path: string,
): { q: string; type?: string } | null {
  const mm = brandModelMap.get(brandSlug);
  if (!mm) return null;

  for (const suffix of TYPE_SUFFIXES) {
    if (path.endsWith(suffix)) {
      const modelSlug = path.slice(0, -suffix.length);
      const q = mm.get(modelSlug);
      if (q) {
        const typePart = suffix.replace("-inventory", "").slice(1); // strip leading '-'
        return { q, type: toInternalType(typePart) };
      }
    }
  }

  // Exact model match (no type suffix)
  const q = mm.get(path);
  if (q) return { q };

  return null;
}

/* ------------------------------------------------------------------ */
/*  Server-side (301) redirect URL builders                            */
/* ------------------------------------------------------------------ */

/** Legacy brand sub-path → canonical /rv-search URL */
export function brandSubRedirectUrl(
  brand: string,
  path?: string,
  trim?: string,
): string {
  if (!path || path === "brochures") return `/${brand}`;
  const resolved = resolveModel(brand, path);
  if (!resolved) return `/${brand}`;
  const sp = new URLSearchParams();
  sp.set("q", trim ? `${resolved.q} ${trim}` : resolved.q);
  if (resolved.type) sp.set("type", resolved.type);
  return `/rv-search?${sp.toString()}`;
}

const RESERVED_PRODUCT_SLUGS: Record<string, string> = {
  kiosk: "/kiosk",
  "door-prizes": "/door-prizes",
  signup: "/community-login",
  register: "/community-login",
};

/**
 * Legacy /product/<slug>[/<feature>] → canonical URL.
 * Unit slugs sometimes carry a trailing legacy type id (…-3283201-29);
 * the canonical /rv-search slug ends at the stock id.
 */
export function productSlugRedirectUrl(slug: string, feature?: string): string {
  const s = (slug || "").toLowerCase();
  if (RESERVED_PRODUCT_SLUGS[s]) return RESERVED_PRODUCT_SLUGS[s];
  if (isKnownType(s)) return typeRedirectUrl(s, undefined, feature);
  const unitSlug = s.replace(/(-\d{5,})-\d{1,3}$/, "$1");
  return `/rv-search/${unitSlug}`;
}

/* ------------------------------------------------------------------ */
/*  Route components                                                   */
/* ------------------------------------------------------------------ */

/** Handles /new-product/:type/:feature? and /used-product/:type/:feature? */
export function ConditionTypeRedirect({
  condition,
}: {
  condition: string;
}) {
  const { type, feature } = useParams<{ type: string; feature: string }>();
  if (type && isKnownType(type)) {
    return <Navigate to={typeRedirectUrl(type, condition, feature)} replace />;
  }
  return <Navigate to={`/rv-search?condition=${condition}`} replace />;
}

/** Handles brand sub-routes: /product-{brand}/:path and /product-{brand}/:path/:trim */
export function BrandSubRoute({ brand }: { brand: string }) {
  const { path, trim } = useParams<{ path: string; trim: string }>();

  if (!path || path === "brochures") {
    return <Navigate to={`/${brand}`} replace />;
  }

  const resolved = resolveModel(brand, path);
  if (resolved) {
    const sp = new URLSearchParams();
    sp.set("q", trim ? `${resolved.q} ${trim}` : resolved.q);
    if (resolved.type) sp.set("type", resolved.type);
    return <Navigate to={`/rv-search?${sp.toString()}`} replace />;
  }

  // Unrecognised sub-path → brand page
  return <Navigate to={`/${brand}`} replace />;
}
